import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useCreateSegment, useSegment, useSegmentPreview, useUpdateSegment } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { Granularity, MatchMode, Rules, SegmentStatus } from '@/api/types';
import { SEGMENT_STATUSES } from '@/api/types';
import { PageHeading } from '@/app/PageHeading';
import { Accordion } from '@/components/arc/accordion/accordion';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { Input } from '@/components/arc/input/input';
import { MetricCard } from '@/components/arc/metric-card/metric-card';
import SegmentedControl from '@/components/arc/segmented-control/segmented-control';
import { Select } from '@/components/arc/select/select';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { Slider } from '@/components/arc/slider/slider';
import { Textarea } from '@/components/arc/textarea/textarea';
import { useToastStack } from '@/components/arc/toast-stack/toast-stack';
import { RuleBuilder, rulesValid } from '@/components/rules/RuleBuilder';
import { useDebounce } from '@/lib/hooks';
import { fmtDuration, labelize } from '@/lib/format';
import { useHref } from '@/module/basePath';
import styles from './SegmentEditorPage.module.css';

interface Prefill {
  name?: string;
  rules?: Rules;
  granularity?: Granularity;
}

const DEFAULT_RULES: Rules = { include: [{ field: 'topics', op: 'contains_any', value: [] }], exclude: [] };

export function SegmentEditorPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const href = useHref();
  const { toast } = useToastStack();
  const loc = useLocation();
  const prefill = (loc.state as { prefill?: Prefill } | null)?.prefill;
  const existing = useSegment(id);
  const create = useCreateSegment();
  const update = useUpdateSegment();

  const [name, setName] = useState(prefill?.name ?? '');
  const [description, setDescription] = useState('');
  const [granularity, setGranularity] = useState<Granularity>(prefill?.granularity ?? 'video');
  const [matchMode, setMatchMode] = useState<MatchMode>('any');
  const [minConf, setMinConf] = useState(0.6);
  const [status, setStatus] = useState<SegmentStatus>('draft');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [rules, setRules] = useState<Rules>(prefill?.rules ?? DEFAULT_RULES);
  const [loaded, setLoaded] = useState(!id);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);

  useEffect(() => {
    if (!existing.data || loaded) return;
    const s = existing.data;
    setName(s.name);
    setDescription(s.description ?? '');
    setGranularity(s.granularity);
    setMatchMode(s.match_mode);
    setMinConf(s.min_confidence);
    setStatus(s.status);
    setWebhookUrl(s.webhook_url ?? '');
    setRules(s.rules);
    setLoaded(true);
  }, [existing.data, loaded]);

  const valid = rulesValid(rules);
  const previewInput = useMemo(
    () => (valid ? { rules, granularity, match_mode: matchMode, min_confidence: minConf } : null),
    [rules, granularity, matchMode, minConf, valid],
  );
  const previewBody = useDebounce(previewInput, 600);
  const preview = useSegmentPreview(previewBody);

  const saving = create.isPending || update.isPending;
  const canSave = name.trim().length > 0 && valid && !saving;

  const save = async () => {
    setSaveError(null);
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      rules,
      granularity,
      match_mode: matchMode,
      min_confidence: minConf,
      status,
      webhook_url: webhookUrl.trim() || null,
    };
    try {
      if (id) {
        await update.mutateAsync({ id, body });
        toast({
          type: 'success',
          title: 'Segment saved',
          description: 'Members are being recomputed in the background.',
        });
        nav(href(`/segments/${id}`));
      } else {
        const s = await create.mutateAsync(body);
        toast({
          type: 'success',
          title: 'Segment created',
          description: 'Members are being computed in the background.',
        });
        nav(href(`/segments/${s.id}`));
      }
    } catch (e) {
      setSaveError(errorMessage(e));
    }
  };

  const backTo = id ? `/segments/${id}` : '/segments';
  const back = (
    <Link to={href(backTo)} className={styles.back}>
      <ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" />
      {id ? 'Back to segment' : 'Back to segments'}
    </Link>
  );

  if (id && existing.isError) {
    return (
      <main className="page">
        <PageHeading title="Edit segment" above={back} />
        <div className="stack-4">
          <Alert tone="danger" title="We could not load this segment">
            {errorMessage(existing.error)}
          </Alert>
          <div>
            <Button type="button" variant="secondary" onClick={() => void existing.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </main>
    );
  }

  if (id && !loaded) {
    return (
      <main className="page" aria-busy="true">
        <PageHeading title="Edit segment" above={back} />
        <Skeleton label="Loading segment" lines={6} />
      </main>
    );
  }

  return (
    <main className="page">
      <PageHeading
        title={id ? 'Edit segment' : 'New segment'}
        description="Rules are evaluated per moment. Video granularity scores each video by the share of its duration that matches."
        above={back}
        actions={
          <Button type="button" loading={saving} disabled={!canSave} onClick={() => void save()}>
            <Save size={16} strokeWidth={1.75} aria-hidden="true" />
            {id ? 'Save and recompute' : 'Create segment'}
          </Button>
        }
      />

      {saveError ? (
        <Alert tone="danger" title="We could not save the segment" onDismiss={() => setSaveError(null)}>
          {saveError}
        </Alert>
      ) : null}

      <div className={styles.layout}>
        <div className="stack-6">
          <section className={styles.section} aria-labelledby="basics-heading">
            <h2 id="basics-heading" className={styles.sectionTitle}>
              Basics
            </h2>
            <div className={styles.form}>
              <Input
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setNameTouched(true)}
                placeholder="e.g. Positive breakfast moments"
                error={nameTouched && !name.trim() ? 'Enter a name for the segment.' : undefined}
              />
              <Textarea
                label="Description"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                description="Optional. Shown in the segment list."
              />
              <div className={styles.controls}>
                <div className={styles.control}>
                  <span className={styles.controlLabel}>Granularity</span>
                  <SegmentedControl
                    label="Granularity"
                    options={[
                      { value: 'video', label: 'Video' },
                      { value: 'moment', label: 'Moment' },
                    ]}
                    value={granularity}
                    onValueChange={(v) => setGranularity(v as Granularity)}
                  />
                </div>
                <div className={styles.control}>
                  <span className={styles.controlLabel}>Match mode</span>
                  <SegmentedControl
                    label="Match mode"
                    options={[
                      { value: 'any', label: 'Any include rule' },
                      { value: 'all', label: 'All include rules' },
                    ]}
                    value={matchMode}
                    onValueChange={(v) => setMatchMode(v as MatchMode)}
                  />
                </div>
              </div>
              <Select
                label="Status"
                value={status}
                onValueChange={(v) => setStatus(v as SegmentStatus)}
                options={SEGMENT_STATUSES.map((s) => ({ value: s, label: labelize(s) }))}
                description="Only active segments can be used in campaigns."
              />
              <Input
                label="Webhook URL"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://dco.example.com/scenewise"
                description="Optional. Receives a signed POST (HMAC-SHA256) after every recompute."
                type="url"
              />
            </div>
          </section>

          <section className="stack-4" aria-labelledby="rules-heading">
            <h2 id="rules-heading" className={styles.sectionTitle}>
              Rules
            </h2>
            <RuleBuilder value={rules} onChange={setRules} />
          </section>

          <Accordion
            defaultOpen={-1}
            items={[
              {
                title: 'Advanced',
                content: (
                  <div className={styles.advanced}>
                    <Slider
                      label="Minimum confidence for brands, products and people"
                      value={minConf}
                      onValueChange={setMinConf}
                      min={0}
                      max={1}
                      step={0.05}
                      format={(v) => v.toFixed(2)}
                    />
                    <p className={styles.advancedHelp}>
                      Only affects rules on brands, products and people: a detection below this confidence does
                      not count as a match. Similarity for semantic rules is set inside the rule itself.
                    </p>
                  </div>
                ),
              },
            ]}
          />
        </div>

        <aside
          className={styles.preview}
          aria-labelledby="preview-heading"
          aria-busy={preview.isFetching || undefined}
        >
          <div className={styles.previewHead}>
            <h2 id="preview-heading" className={styles.sectionTitle}>
              Live preview
            </h2>
            <span className="text-muted text-xs" aria-live="polite">
              {valid && preview.isFetching ? 'Updating' : ''}
            </span>
          </div>
          {!valid ? (
            <p className="text-secondary">Complete at least one rule to see matching counts.</p>
          ) : preview.isError ? (
            <Alert tone="danger" title="Preview failed">
              {errorMessage(preview.error)}
            </Alert>
          ) : preview.data ? (
            <div className="stack-4">
              <div className={styles.previewKpis}>
                <MetricCard label="Videos" value={preview.data.video_total} context="match these rules" />
                <MetricCard label="Moments" value={preview.data.moment_total} context="match these rules" />
              </div>
              <div className={styles.sample}>
                {preview.data.sample.length === 0 ? (
                  <p className="text-muted">No matches yet. Loosen a rule or lower a threshold.</p>
                ) : (
                  <ul className={`${styles.sampleList} rows`}>
                    {preview.data.sample.map((m, i) => (
                      <li key={`${m.video_id}-${m.moment_id ?? i}`} className={styles.sampleRow}>
                        <span className={`${styles.sampleScore} mono`}>{Math.round(m.score * 100)}%</span>
                        <span className={styles.sampleText}>
                          <Link
                            to={href(
                              `/videos/${m.video_id}${m.start_sec != null ? `?t=${Math.floor(m.start_sec)}` : ''}`,
                            )}
                            className={styles.sampleTitle}
                          >
                            {m.video_title ?? m.video_external_id ?? m.video_id}
                          </Link>
                          {m.start_sec != null ? (
                            <span className={styles.sampleMeta}>
                              {fmtDuration(m.start_sec)} to {fmtDuration(m.end_sec)}
                              {m.scene_summary ? `, ${m.scene_summary}` : ''}
                            </span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <Skeleton label="Loading preview" lines={4} />
          )}
        </aside>
      </div>
    </main>
  );
}
